import { Dialog, DialogWindow } from '../../../../../../widgets/dialog';
import { Icon } from '../../../../../../widgets/icon';
import React, { FC } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { T } from 'src/shared/helpers';
import GroupIcon from 'src/shared/material_icons/action/join_inner.svg';
import StatesIcon from 'src/shared/material_icons/action/swap_horiz.svg';
import PartsIcon from 'src/shared/material_icons/action/view_in_ar.svg';
import PresetIcon from 'src/shared/material_icons/action/view_quilt.svg';
import TextIcon from 'src/shared/material_icons/content/font_download.svg';
import ImportIcon from 'src/shared/material_icons/file/download.svg';
import SparklesIcon from 'src/shared/material_icons/image/auto_awesome.svg';
import ImageIcon from 'src/shared/material_icons/image/image.svg';
import PaletteIcon from 'src/shared/material_icons/image/palette.svg';
import AttributeIcon from 'src/shared/material_icons/image/tune.svg';
import LayersIcon from 'src/shared/material_icons/maps/layers.svg';
import StairsIcon from 'src/shared/material_icons/places/stairs.svg';
import styled from 'styled-components';
import type { composerAddGalleryDialogPrintTypesQuery } from './__generated__/composerAddGalleryDialogPrintTypesQuery.graphql';

export type ComposerUseCaseKey = 'color' | 'showhide' | 'engraving' | 'image' | 'state' | 'compound';
export type ComposerBuildCardKey = 'attribute' | 'group' | 'step' | 'import' | 'preset';

interface GalleryCard {
    key: ComposerUseCaseKey | ComposerBuildCardKey;
    name: string;
    description: string;
    icon: React.ReactNode;
    disabled?: boolean;
}

const printTypesQuery = graphql`
    query composerAddGalleryDialogPrintTypesQuery {
        printTypes(first: 200) {
            edges {
                node {
                    printTypeID
                }
            }
        }
    }
`;

const GalleryWindow = styled(DialogWindow)`
    width: min(880px, calc(100vw - 40px));
    max-width: 880px;
    max-height: min(880px, calc(100vh - 48px));
    flex-basis: 880px;
    padding: 0;
    overflow: hidden;
    border-radius: 12px;
    box-shadow: 0 20px 52px rgb(30 42 56 / 24%);

    > h1 {
        display: flex;
        height: 44px;
        flex: 0 0 44px;
        align-items: center;
        margin: 0;
        padding: 0 16px;
        color: var(--composer-fg, #1e2a38);
        border-bottom: 1px solid var(--composer-line, #dce3eb);
        font-size: 13.5px;
    }

    > div:nth-of-type(2) {
        border-top: 1px solid var(--composer-line, #dce3eb);

        > div {
            padding: 12px 16px;
        }

        button {
            min-width: 0;
        }
    }
`;

const GalleryBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    overflow: auto;
    padding: 14px 16px;
`;

const Lead = styled.p`
    margin: 0;
    color: var(--composer-muted, #5c6b7a);
    font-size: 12.5px;
    line-height: 1.5;
`;

const Group = styled.section`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const GroupTitle = styled.h3`
    margin: 0;
    color: var(--composer-faint, #677078);
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
`;

const Cards = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
    gap: 10px;

    @media (max-width: 760px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
`;

const Card = styled.button<{ $disabled?: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    padding: 11px;
    color: var(--composer-fg, #1e2a38);
    background: var(--composer-panel, #fff);
    border: 1px solid var(--composer-line, #dce3eb);
    border-radius: 8px;
    cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
    opacity: ${(props) => (props.$disabled ? 0.55 : 1)};
    text-align: left;

    &:hover,
    &:focus-visible {
        background: var(--composer-hover, #e9eff5);
        border-color: var(--composer-faint, #677078);
        outline: none;
    }

    &[aria-disabled='true']:hover,
    &[aria-disabled='true']:focus-visible {
        background: var(--composer-panel, #fff);
        border-color: var(--composer-line, #dce3eb);
    }
`;

const CardIcon = styled.span`
    display: inline-flex;
    width: 30px;
    height: 30px;
    align-items: center;
    justify-content: center;
    color: var(--composer-muted, #5c6b7a);
    background: var(--composer-sunken, #f4f7fa);
    border-radius: 5px;

    ${Icon} {
        width: 18px;
        height: 18px;
    }
`;

const CardText = styled.span`
    display: flex;
    flex-direction: column;
    gap: 6px;

    strong {
        font-size: 12.5px;
        line-height: 16px;
    }

    span {
        color: var(--composer-faint, #677078);
        font-size: 11px;
        line-height: 1.45;
    }
`;

const AIBand = styled(Card)`
    width: 100%;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    padding: 12px 14px;
    color: var(--composer-ai, #6b36ce);
    background: var(--composer-ai-wash, #f7f3fe);
    border-color: var(--composer-ai, #6b36ce);

    &:hover,
    &:focus-visible {
        background: var(--composer-ai-wash-hover, #f0e8fd);
        border-color: var(--composer-ai, #6b36ce);
    }

    ${CardIcon} {
        width: 26px;
        height: 26px;
        color: inherit;
        background: none;

        ${Icon} {
            width: 22px;
            height: 22px;
        }
    }

    ${CardText} {
        gap: 2px;

        strong {
            font-size: 14px;
            line-height: 20px;
        }

        span {
            color: var(--composer-muted, #5c6b7a);
            font-size: 12.5px;
        }
    }
`;

const getUseCases = (): GalleryCard[] => [
    {
        key: 'color',
        name: T._('Colours & finishes', 'Admin'),
        description: T._('Sell the same product in more than one colour or material', 'Admin'),
        icon: <PaletteIcon />
    },
    {
        key: 'showhide',
        name: T._('Parts on or off', 'Admin'),
        description: T._('Build versions by bringing pieces of the model in and out of view', 'Admin'),
        icon: <PartsIcon />
    },
    {
        key: 'engraving',
        name: T._('Personalised text', 'Admin'),
        description: T._('Let the buyer write something that ends up printed on the product', 'Admin'),
        icon: <TextIcon />
    },
    {
        key: 'image',
        name: T._("Buyer's own image", 'Admin'),
        description: T._('Let the buyer upload a picture and see it on the product', 'Admin'),
        icon: <ImageIcon />
    },
    {
        key: 'state',
        name: T._('Positions & states', 'Admin'),
        description: T._('Show the product open, closed, folded — one shape per choice', 'Admin'),
        icon: <StatesIcon />
    },
    {
        key: 'compound',
        name: T._('Two changes in one', 'Admin'),
        description: T._('A single choice that moves more than one thing at the same time', 'Admin'),
        icon: <LayersIcon />
    }
];

const getBuildCards = (): GalleryCard[] => [
    {
        key: 'attribute',
        name: T._('Empty attribute', 'Admin'),
        description: T._('A choice with no options yet and no effect on the model — you name it and fill it', 'Admin'),
        icon: <AttributeIcon />
    },
    {
        key: 'group',
        name: T._('Empty group', 'Admin'),
        description: T._('A folder that gathers attributes under one heading', 'Admin'),
        icon: <GroupIcon />
    },
    {
        key: 'step',
        name: T._('New step', 'Admin'),
        description: T._('A stage the buyer goes through, with the attributes it asks for', 'Admin'),
        icon: <StairsIcon />
    },
    {
        key: 'import',
        name: T._('Copy from another product', 'Admin'),
        description: T._('Bring over a structure you have already built somewhere else', 'Admin'),
        icon: <ImportIcon />
    },
    {
        key: 'preset',
        name: T._('Ready-made configurations', 'Admin'),
        description: T._('Whole set-ups the buyer takes in one go', 'Admin'),
        icon: <PresetIcon />,
        disabled: true
    }
];

const GalleryCardButton: FC<{ card: GalleryCard; onClick: () => void }> = ({ card, onClick }) => (
    <Card type='button' $disabled={card.disabled} aria-disabled={card.disabled || undefined} onClick={onClick}>
        <CardIcon>
            <Icon>{card.icon}</Icon>
        </CardIcon>
        <CardText>
            <strong>{card.name}</strong>
            <span>{card.description}</span>
        </CardText>
    </Card>
);

interface ComposerAddGalleryDialogProps {
    onUseCase: (key: ComposerUseCaseKey, printingMethodID: string) => void;
    onBuild: (key: ComposerBuildCardKey) => void;
    onAI: () => void;
    onUnavailable: (name: string) => void;
    onCancel: () => void;
}

const ComposerAddGalleryDialog: FC<ComposerAddGalleryDialogProps> = ({ onUseCase, onBuild, onAI, onUnavailable, onCancel }) => {
    const data = useLazyLoadQuery<composerAddGalleryDialogPrintTypesQuery>(printTypesQuery, {}, { fetchPolicy: 'store-or-network' });
    const printingMethodID = String(data.printTypes?.edges?.[0]?.node?.printTypeID ?? '');

    return (
        <Dialog
            title={T._('Add a customization', 'Admin')}
            windowDecorator={GalleryWindow}
            buttons={[{ label: T._('Cancel', 'Admin'), outline: true, onClick: onCancel }]}
        >
            <GalleryBody>
                <Lead>{T._('Pick what you want the buyer to choose. We set it up, and everything stays editable after.', 'Admin')}</Lead>
                <Group>
                    <GroupTitle>{T._('Start from what you want to offer', 'Admin')}</GroupTitle>
                    <Cards>
                        {getUseCases().map((card) => (
                            <GalleryCardButton key={card.key} card={card} onClick={() => onUseCase(card.key as ComposerUseCaseKey, printingMethodID)} />
                        ))}
                    </Cards>
                </Group>
                <Group>
                    <GroupTitle>{T._('Or build the structure yourself', 'Admin')}</GroupTitle>
                    <Cards>
                        {getBuildCards().map((card) => (
                            <GalleryCardButton
                                key={card.key}
                                card={card}
                                onClick={() => (card.disabled ? onUnavailable(card.name) : onBuild(card.key as ComposerBuildCardKey))}
                            />
                        ))}
                    </Cards>
                </Group>
                <Group>
                    <GroupTitle>{T._('Or describe it and let AI build it', 'Admin')}</GroupTitle>
                    <AIBand type='button' onClick={onAI}>
                        <CardIcon>
                            <Icon>
                                <SparklesIcon />
                            </Icon>
                        </CardIcon>
                        <CardText>
                            <strong>{T._('Generate with AI', 'Admin')}</strong>
                            <span>{T._('Describe the product in your own words and get attributes and options, ready to edit', 'Admin')}</span>
                        </CardText>
                    </AIBand>
                </Group>
            </GalleryBody>
        </Dialog>
    );
};

export default ComposerAddGalleryDialog;
